import { userManager } from "../auth/oidc";
import { ApiError, call } from "./client";
import type { UserBody, UserMade, UsersView } from "./types";

const base = (import.meta.env.VITE_API_BASE as string) || "";

// ---------------------------------------------------------------------------
// The admin API's shapes. Hand-written rather than generated: these endpoints
// are the panel's own and change with it, and a round trip through the spec
// for each edit would slow that down without catching anything the catalogue
// types already catch.
// ---------------------------------------------------------------------------

export type ParamKind = "int" | "float" | "bool" | "string" | "strings" | "date";

export type ParamSpec = {
  name: string;
  kind: ParamKind;
  label: string;
  help?: string;
  default?: unknown;
  min?: number;
  max?: number;
  options?: string[];
};

export type Run = {
  id: number;
  kind: string;
  params: Record<string, unknown>;
  state: "queued" | "running" | "succeeded" | "failed" | "cancelled";
  queuedAt: string;
  startedAt?: string;
  finishedAt?: string;
  cancelRequested: boolean;
  progressDone?: number;
  progressTotal?: number;
  progressNote?: string;
  counters: Record<string, number>;
  error?: string;
  triggeredBy: string;
  scheduleId?: number;
};

export type JobView = {
  kind: string;
  title: string;
  summary: string;
  params: ParamSpec[];
  writes?: string[];
  hosts?: string[];
  available: boolean;
  note?: string;
  defaults: Record<string, unknown>;
  running?: Run;
  last?: Run;
  nextRun?: string;
};

export type Schedule = {
  id: number;
  kind: string;
  cron: string;
  params: Record<string, unknown>;
  enabled: boolean;
  lastRunAt?: string;
  nextRunAt?: string;
};

export type HostBudget = {
  host: string;
  perMinute: number;
  hardFloor: boolean;
  note?: string;
};

export type Anomaly = {
  kind: string;
  subject: string;
  detail: string;
  at: string;
};

export type LogLine = {
  id: number;
  at: string;
  level: string;
  /** The closed vocabulary from the backend's internal/logcat. */
  category: string;
  msg: string;
  fields?: Record<string, unknown>;
};

/** A log line from the searchable history, which has to name its own run. */
export type LogHit = LogLine & { runId: number; kind: string };

export type LogSearch = {
  lines: LogHit[];
  total: number;
  /** The count stopped at its ceiling; there are at least `total` matches. */
  capped: boolean;
  limit: number;
  offset: number;
};

export type LogCategory = {
  name: string;
  level: string;
  group: string;
  label: string;
  help?: string;
  rank: number;
  count: number;
};

export type LogQuery = {
  q?: string;
  category?: string[];
  level?: string[];
  kind?: string[];
  runId?: number;
  since?: string;
  until?: string;
  order?: "time" | "category";
  limit?: number;
  offset?: number;
};

/** One kind of missing information, and how much of it there is. */
export type Gap = {
  category: string;
  subject: "release" | "track";
  title: string;
  detail: string;
  fix?: string;
  terminal?: boolean;
  count: number;
};

/** One release, or one track, that is missing something. */
export type GapRow = {
  key: string;
  artist: string;
  title: string;
  label?: string;
  url?: string;
  date?: string;
  shops: string[];
  pos?: number;
  track?: string;
  detail?: string;
};

export type GapRows = {
  kind: Omit<Gap, "count">;
  rows: GapRow[];
  total: number;
  limit: number;
  offset: number;
};

export const admin = {
  jobs: () => call<JobView[]>("/api/admin/jobs"),
  run: (kind: string, params: Record<string, unknown>) =>
    call<{ runId: number }>(`/api/admin/jobs/${kind}/run`, {
      method: "POST",
      body: JSON.stringify(params),
    }),
  saveDefaults: (kind: string, params: Record<string, unknown>) =>
    call<Record<string, unknown>>(`/api/admin/jobs/${kind}/defaults`, {
      method: "PUT",
      body: JSON.stringify(params),
    }),

  runs: (q?: { kind?: string; state?: string; limit?: number }) => {
    const p = new URLSearchParams();
    if (q?.kind) p.set("kind", q.kind);
    if (q?.state) p.set("state", q.state);
    if (q?.limit) p.set("limit", String(q.limit));
    return call<Run[]>(`/api/admin/runs${[...p].length ? `?${p}` : ""}`);
  },
  runDetail: (id: number) => call<Run>(`/api/admin/runs/${id}`),
  /** Stop a running run at its next checkpoint, or take a queued one out of the queue. */
  cancel: (id: number) =>
    call<{ cancelRequested: boolean; unqueued?: boolean }>(`/api/admin/runs/${id}/cancel`, { method: "POST" }),

  schedules: () => call<Schedule[]>("/api/admin/schedules"),
  createSchedule: (s: Partial<Schedule>) =>
    call<{ id: number }>("/api/admin/schedules", { method: "POST", body: JSON.stringify(s) }),
  updateSchedule: (id: number, s: Partial<Schedule>) =>
    call<void>(`/api/admin/schedules/${id}`, { method: "PUT", body: JSON.stringify(s) }),
  deleteSchedule: (id: number) =>
    call<void>(`/api/admin/schedules/${id}`, { method: "DELETE" }),

  hosts: () => call<HostBudget[]>("/api/admin/hosts"),
  setHost: (host: string, perMinute: number) =>
    call<HostBudget[]>(`/api/admin/hosts/${encodeURIComponent(host)}`, {
      method: "PUT",
      body: JSON.stringify({ perMinute }),
    }),

  anomalies: () => call<Anomaly[]>("/api/admin/anomalies"),

  // The realm's users. The shapes come from the spec, unlike the rest of this
  // file: a password is handed back exactly once, and that is worth a type.
  users: () => call<UsersView>("/api/admin/users"),
  createUser: (b: UserBody) =>
    call<UserMade>("/api/admin/users", { method: "POST", body: JSON.stringify(b) }),
  resetPassword: (id: string, password?: string) =>
    call<UserMade>(`/api/admin/users/${encodeURIComponent(id)}/password`, {
      method: "PUT", body: JSON.stringify(password ? { password } : {}),
    }),
  setRole: (id: string, role: "user" | "admin") =>
    call<void>(`/api/admin/users/${encodeURIComponent(id)}/role`, {
      method: "PUT", body: JSON.stringify({ role }),
    }),
  setEnabled: (id: string, enabled: boolean) =>
    call<void>(`/api/admin/users/${encodeURIComponent(id)}/enabled`, {
      method: "PUT", body: JSON.stringify({ enabled }),
    }),
  setRights: (id: string, rights: string[]) =>
    call<UserMade>(`/api/admin/users/${encodeURIComponent(id)}/rights`, {
      method: "PUT", body: JSON.stringify({ rights }),
    }),

  logs: (q: LogQuery) => {
    const p = new URLSearchParams();
    if (q.q) p.set("q", q.q);
    // Repeated rather than comma-joined: a category name is ours and has no
    // commas in it, but a free-text search term might, and one encoding for
    // both is one fewer thing to get wrong.
    q.category?.forEach((c) => p.append("category", c));
    q.level?.forEach((l) => p.append("level", l));
    q.kind?.forEach((k) => p.append("kind", k));
    if (q.runId) p.set("runId", String(q.runId));
    if (q.since) p.set("since", q.since);
    if (q.until) p.set("until", q.until);
    if (q.order) p.set("order", q.order);
    if (q.limit) p.set("limit", String(q.limit));
    if (q.offset) p.set("offset", String(q.offset));
    return call<LogSearch>(`/api/admin/logs${[...p].length ? `?${p}` : ""}`);
  },
  logCategories: () => call<LogCategory[]>("/api/admin/log-categories"),

  gaps: () => call<Gap[]>("/api/admin/gaps"),
  gapRows: (category: string, q?: { q?: string; limit?: number; offset?: number }) => {
    const p = new URLSearchParams();
    if (q?.q) p.set("q", q.q);
    if (q?.limit) p.set("limit", String(q.limit));
    if (q?.offset) p.set("offset", String(q.offset));
    return call<GapRows>(
      `/api/admin/gaps/${encodeURIComponent(category)}${[...p].length ? `?${p}` : ""}`,
    );
  },
};

/**
 * Follow a run's log.
 *
 * EventSource cannot send an Authorization header, and everything here is
 * behind the realm -- so this reads the SSE stream off a fetch body instead.
 * A little more code than `new EventSource(url)`, and it keeps the bearer
 * token out of the query string where it would end up in access logs.
 */
export async function followRun(
  id: number,
  on: { log: (l: LogLine) => void; state: (r: Partial<Run>) => void; end: () => void },
  signal: AbortSignal,
): Promise<void> {
  const user = await userManager.getUser();
  const res = await fetch(`${base}/api/admin/runs/${id}/logs`, {
    headers: user?.access_token ? { Authorization: `Bearer ${user.access_token}` } : {},
    signal,
  });
  if (!res.ok || !res.body) throw new ApiError(res.status, null, `HTTP ${res.status}`);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line; a partial one stays buffered.
    let split: number;
    while ((split = buffer.indexOf("\n\n")) >= 0) {
      const frame = buffer.slice(0, split);
      buffer = buffer.slice(split + 2);

      let event = "message";
      let data = "";
      for (const line of frame.split("\n")) {
        if (line.startsWith("event: ")) event = line.slice(7);
        else if (line.startsWith("data: ")) data += line.slice(6);
      }
      if (!data) continue; // a keepalive comment
      try {
        const parsed = JSON.parse(data);
        if (event === "log") on.log(parsed as LogLine);
        else if (event === "state") on.state(parsed as Partial<Run>);
        else if (event === "end") { on.end(); return; }
      } catch { /* a frame we cannot parse is not worth dying over */ }
    }
  }
  on.end();
}
